/**
 * 把 SSE 的 ledger 事件攒成「某一轮的过程步骤」。
 *
 * **判断全在这些纯函数里**，React 那侧只管把事件喂进来——
 * 与 `stream.ts` / `use-stream.ts` 那对分工一样。理由是这一段的规矩
 * （去重、排序、只收 run.* 、按 run 分组）要能在不起浏览器、
 * 不连服务端的情况下逐条验。
 *
 * # 三条规矩
 *
 *  1. **不重复、不漏、不乱序。** 合同第三节点名要的。判据是 sequence：
 *     它在库里按会话单调（`PRIMARY KEY (conversation_id, sequence)`），
 *     而 SSE 的 `id:` 就是它。同一个 sequence 收两次只留一条；
 *     乱序到达按 sequence 排回去。
 *  2. **只收 `run.*` 或服务端闭集合投影。** plan/work_item/agent/settlement
 *     只有同时带真实 work_plan run_id、development run_kind 与 run_step
 *     才能进入过程卡；客户端不从事件名字自行猜。
 *  3. **认不出的整条丢掉。** 缺 run_id、缺 payload、state 不在词表里，
 *     一律不收。**不补默认值**：一条画不出真状态的步骤，
 *     画出来就是在编。
 */

/** 一步的真实状态。与服务端 blocks_run.go / runevents.go 同一份词表。 */
export const RUN_STEP_STATES = ['running', 'succeeded', 'failed', 'waiting', 'skipped'] as const

export type RunStepState = (typeof RUN_STEP_STATES)[number]

export interface RunStep {
  readonly sequence: number
  readonly runId: string
  readonly at: string
  readonly actor: string
  readonly event: string
  readonly summary: string
  readonly state: RunStepState
  readonly objectId?: string
  /** 这一轮到这条事件为止烧掉的 token（payload.tokens，累计值）；没有就是 undefined。 */
  readonly tokens?: number
}

/** 一条 ledger SSE 事件解出来的样子。**字段全部可疑**——它来自网络。 */
interface LedgerWire {
  sequence?: unknown
  event_type?: unknown
  actor_id?: unknown
  created_at?: unknown
  run_id?: unknown
  run_kind?: unknown
  run_step?: unknown
  payload?: unknown
}

const DEVELOPMENT_RUN_EVENTS = new Set([
  'plan.proposed',
  'plan.confirmed',
  'work_item.created',
  'work_item.assigned',
  'work_item.ready',
  'work_item.runtime_started',
  'work_item.waiting',
  'work_item.blocked',
  'work_item.recovering',
  'work_item.reassigned',
  'work_item.completed',
  'work_item.failed',
  'work_item.cancelled',
  'agent.joined',
  'agent.subagent_spawned',
  'agent.checkpointed',
  'agent.recovered',
  'agent.session_completed',
  'agent.disconnected',
  'agent.fenced',
  'agent.session_failed',
  'agent.session_cancelled',
  'settlement.result_available',
])

function isRunStepState(v: unknown): v is RunStepState {
  return typeof v === 'string' && (RUN_STEP_STATES as readonly string[]).includes(v)
}

function text(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() !== '' ? v : undefined
}

/**
 * parseRunStep 把一条 ledger 事件解成一步。
 *
 * **认不出就回 null**，调用方整条丢掉。这里不补任何默认值：
 * 一条 actor 缺失的步骤补成「Lulu」，就是把别人做的事记在它头上——
 * 而那正是合同点名禁止的（「多 Agent 明确显示是谁在做」）。
 */
export function parseRunStep(raw: string): RunStep | null {
  let wire: LedgerWire
  try {
    wire = JSON.parse(raw) as LedgerWire
  } catch {
    return null
  }
  const eventType = text(wire.event_type)
  if (eventType === undefined) return null
  const nativeRun = eventType.startsWith('run.')
  const projectedDevelopmentRun =
    DEVELOPMENT_RUN_EVENTS.has(eventType) && wire.run_kind === 'development'
  if (!nativeRun && !projectedDevelopmentRun) return null

  const runId = text(wire.run_id)
  const actor = text(wire.actor_id)
  const at = text(wire.created_at)
  const sequence =
    typeof wire.sequence === 'number' && Number.isFinite(wire.sequence) ? wire.sequence : undefined
  if (runId === undefined || actor === undefined || at === undefined || sequence === undefined) {
    return null
  }
  const stepSource = nativeRun ? wire.payload : wire.run_step
  const payload =
    typeof stepSource === 'object' && stepSource !== null
      ? (stepSource as Record<string, unknown>)
      : undefined
  if (payload === undefined) return null
  const summary = text(payload.summary)
  const state = payload.state
  if (summary === undefined || !isRunStepState(state)) return null

  return {
    sequence,
    runId,
    at,
    actor,
    event: nativeRun ? eventType.slice('run.'.length) : eventType,
    summary,
    state,
    objectId: text(payload.object_id),
    ...(typeof payload.tokens === 'number' && Number.isFinite(payload.tokens) && payload.tokens > 0
      ? { tokens: payload.tokens }
      : {}),
  }
}

/**
 * mergeRunSteps 把新到的步骤并进已有的那些。
 *
 * **不重复、不漏、不乱序**都在这一个函数里：
 *
 *   不重复  按 sequence 去重（同一条重连时会再来一次）
 *   不乱序  并完按 sequence 排
 *   不漏    这里做不到——漏没漏由 Last-Event-ID 那侧保证，
 *           而这个函数只保证**它收到的都留下了**
 *
 * 最后那一句要说清：一个函数不该声称它管不着的事。
 * 续传的完整性靠的是浏览器带 Last-Event-ID + 服务端从那个游标之后重发，
 * 这里只是不把收到的弄丢。
 */
export function mergeRunSteps(
  existing: readonly RunStep[],
  incoming: readonly RunStep[],
): RunStep[] {
  if (incoming.length === 0) return existing as RunStep[]
  const bySeq = new Map<number, RunStep>()
  for (const s of existing) bySeq.set(s.sequence, s)
  for (const s of incoming) bySeq.set(s.sequence, s)
  return [...bySeq.values()].sort((a, b) => a.sequence - b.sequence)
}

/** stepsForRun 取某一轮的步骤。**按 run 分组**，不同轮不混在一起。 */
export function stepsForRun(all: readonly RunStep[], runId: string | undefined): RunStep[] {
  if (runId === undefined || runId === '') return []
  return all.filter((s) => s.runId === runId)
}

/**
 * runIsFinished 说这一轮收口了没有。
 *
 * 判据是**收到过 `run.finished`**——一个真实事件，
 * 不是「多久没有新步骤了」那种超时推断。
 * 超时推断会在一条慢任务上把「还在跑」说成「结束了」。
 */
export function runIsFinished(steps: readonly RunStep[]): boolean {
  return steps.some((s) => s.event === 'finished' || s.event === 'settlement.result_available')
}
